<template lang='pug'>
  .bp-container(:class='{ "no-top-padding": isMobileApp }')
    account-nav(v-if='isSignedInOnWeb')
    .bp-section
      .title(v-if='!!currentUser && !isMobile'
             v-text='$t("account.beautyPassBenefits.title")')
      .header-section(ref='headerSection' class='new-banner')
        template
          .header-wrapper(:class='{ "with-user": currentUser }')
            .h5(v-text='$t("account.sephoraBeautyPass.title")')
            template(v-if='!!currentUser')
              account-progress(:user='currentUser.attributes')
            template(v-else)
              guest-header(:earnAmountText='amountEquivalentToLoyaltyPoint')

      .content-section
        template(v-if='pageLoaded')
          rewards-list(:title='rewardsTitle'
                       :description='rewardsDescription',
                       :hideDescription='true')
          hr.hr
          gifts-and-services(:title='giftsTitle'
                             :description='giftsDescription'
                             :birthdayImageUrl='giftsBirthdayImageUrl'
                             :tierUpgradeImageUrl='giftsTierUpgradeImageUrl',
                             :hideDescription='true')
          hr.hr
          accumulate-points(:title='pointsTitle'
                            :description='pointsDescription'
                            :points='points',
                            :hideDescription='true')
          hr.hr
          membership-levels(:membership='membership')
          hr.hr
        how-it-works(:earnAmountText='amountEquivalentToLoyaltyPoint')

        template(v-if='pageLoaded && !currentUser')
          hr.hr
          join-beauty-pass

        a.faqs(:href='viewAllFaqsLink')
          .text(v-text='$t("account.beautyPassBenefits.viewAllFaqs")')
          .arrow
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import AccountNav from '../nav.vue'
import AccountProgress from './account-progress.vue'
import AccumulatePoints from './points.vue'
import GiftsAndServices from './gifts.vue'
import GuestHeader from './guest-header.vue'
import HowItWorks from './how-it-works.vue'
import JoinBeautyPass from './join.vue'
import MembershipLevels from './membership.vue'
import RewardsList from './rewards.vue'

import beautyPass from '@/store/modules/beauty-pass'

import { dynamicLoadStore } from '@/mixins/store-util'
import { fullUrl } from '@/modules/services/utilities'
import { notificationMixin } from '@/mixins/notificationMixin'

export default {
  components: {
    AccountNav,
    AccountProgress,
    AccumulatePoints,
    GiftsAndServices,
    GuestHeader,
    HowItWorks,
    JoinBeautyPass,
    MembershipLevels,
    RewardsList
  },

  mixins: [
    dynamicLoadStore({ beautyPass }),
    notificationMixin
  ],

  data () {
    return {
      country: null,
      headerWidth: 1200
    }
  },

  computed: {
    ...mapGetters(['isMobileApp']),
    ...mapState({
      currentUser: state => state.user.currentUser,
      pageLoaded: state => state.beautyPass.pageLoaded,
      bannerImageUrl: state => state.beautyPass.bannerImageUrl,
      bannerImageMobileWebUrl: state => state.beautyPass.bannerImageMobileWebUrl,

      points: state => state.beautyPass.points,
      membership: state => state.beautyPass.membership,

      rewardsTitle: state => state.beautyPass.rewardsTitle,
      rewardsDescription: state => state.beautyPass.rewardsDescription,
      giftsTitle: state => state.beautyPass.giftsTitle,
      giftsDescription: state => state.beautyPass.giftsDescription,
      pointsTitle: state => state.beautyPass.pointsTitle,
      pointsDescription: state => state.beautyPass.pointsDescription,

      giftsBirthdayImageUrl: state => state.beautyPass.giftsBirthdayImageUrl,
      giftsTierUpgradeImageUrl: state => state.beautyPass.giftsTierUpgradeImageUrl
    }),
    isMobile: () => vueSupport.isMobile,

    isSignedInOnWeb () {
      return !!this.currentUser && !this.isMobileApp
    },

    headerSectionBannerImageUrl () {
      // 576 is Style Guide's sm breakpoint
      if (window.screen.width >= 576) {
        return this.bannerImageUrl
      } else {
        return this.bannerImageMobileWebUrl
      }
    },

    // Tricky positioning of hero image in the header due to the sidebar for
    // logged in users. Adjust hero image positioning based on device
    heroPosition () {
      if (this.userOnTabletAndSmallDesktop || this.guestOnTablet) {
        return { backgroundPosition: 'center' }
      }
      return {}
    },

    userOnTabletAndSmallDesktop () {
      return !!this.currentUser && this.headerWidth >= 576 && this.headerWidth <= 992
    },

    guestOnTablet () {
      return !!this.currentUser && this.headerWidth >= 576 && this.headerWidth <= 768
    },

    amountEquivalentToLoyaltyPoint () {
      switch (I18n.siteCountryLs) {
        case 'th':
          return '฿ 25'
        case 'id':
          return 'Rp 10.000'
        case 'hk':
          return 'HK$5'
        case 'my':
          return 'MYR 3'
        case 'nz':
          return 'NZ$1'
        case 'ph':
          return '₱35'
        case 'kr':
          return '1,000원'
        case 'bn':
          return 'BN$1'
        case 'bd':
          return 'Tk65'
        case 'jp':
          return '¥80'
        case 'la':
          return '₭7000'
        case 'mm':
          return 'MMK1100'
        case 'pk':
          return 'Rs110'
        case 'lk':
          return 'SLRs150'
        case 'tw':
          return 'NT$20'
        case 'vn':
          return '₫17000'
        default:
          return '$1'
      }
    },

    viewAllFaqsLink () {
      return (this.isMobileApp ? `sephora://faqs/${this.faqSection}` : `/faqs/${this.faqSection}`)
    },

    faqSection () {
      return `${this.$t('faq.links.beautyPassSection', I18n.siteCountryLs)}`
    }
  },

  mounted () {
    this.headerWidth = this.$refs.headerSection.clientWidth
  },

  created () {
    this.fetchPage()
    this.displayKRShutdownMessage()
    this.$store.commit('setSeoMetaTitle', 'Sephora Beauty Pass Rewards Programme')
    this.$store.commit('setSeoMetaDescription', 'Beauty Pass is free to join and every $1 spent earns you a point closer to your next beauty reward. Join now for member exclusives, birthday gifts and more.')
    this.$store.commit('setSeoOgUrl', fullUrl(this.$route.path))
    this.$store.commit('setSeoUpdate', true)
  },

  methods: {
    fetchPage () {
      this.$store.dispatch('fetchPage')
    }
  }
}
</script>

<style lang='scss' scoped>
  .bp-container {
    margin-left: -10px;
    margin-right: -10px;
    text-align: center;

    @include media-breakpoint-up(sm) {
      display: flex;
      margin: 0;
      padding-top: 30px;
    }

    > .bp-section {
      flex-grow: 1;
    }

    &.no-top-padding {
      padding-top: 0;

      .bp-section .header-section {
        margin-top: 0;
      }
    }
  }

  .bp-section {
    > .title {
      display: none;

      @include media-breakpoint-up(sm) {
        @include font-weight-medium;
        display: block;
        text-align: left;
      }
    }

    > .header-section {
      background-size: cover;
      display: flex;
      flex-direction: column;
      height: 200px;
      justify-content: center;
      margin-bottom: 80px;
      width: 100%;

      @include media-breakpoint-up(lg) {
        margin-top: 5px;
      }

      @include media-breakpoint-down(lg) {
        height: 250px;
      }

      @include media-breakpoint-up(xl) {
        height: 325px;
        margin-bottom: 0;
        padding-top: 50px;
        &.new-banner {
          padding-top: 0;
        }
      }

      &.new-banner {
        background-image: url('../../../assets/images/account/beauty_pass/REG_BPRef_BPPG_Mobile_HeroImg.jpg');

        @include media-breakpoint-up(sm) {
          background-size: contain;
          background-image: url('../../../assets/images/account/beauty_pass/REG_BPRef_BPPG_Tablet_HeroImg.jpg');
        }

        @include media-breakpoint-up(md) {
          background-size: contain;
          background-image: url('../../../assets/images/account/beauty_pass/REG_BPRef_BPPG_Web_HeroImg.jpg');
        }

        @include media-breakpoint-up(lg) {
          background-image: url('../../../assets/images/account/beauty_pass/REG_BPRef_BPPG_Web_HeroImg.jpg');
        }

        @include media-breakpoint-up(xl) {
          background-image: url('../../../assets/images/account/beauty_pass/REG_BPRef_BPPG_Web_HeroImg.jpg');
        }
      }

      .h5 {
        @include media-breakpoint-down(sm) {
          font-size: 30px;
        }
      }
    }

    > .content-section {
      max-width: 920px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .header-section {
    > .logo {
      background-image: url('../../../assets/images/account/beauty_pass/logo.png');
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
      height: 20%;
      margin-left: auto;
      margin-right: auto;
      position: relative;
      top: 55px;
      width: 100%;

      @include media-breakpoint-up(sm) {
        height: 26%;
        top: 65px;
      }

      @include media-breakpoint-up(xl) {
        height: 26%;
        position: static;
      }
    }

    .h5 {
      @include font-sephora-serif;
      font-size: 40px;
      line-height: 32px;
      margin-left: auto;
      margin-right: auto;
      text-transform: uppercase;
      width: 80%;

      @include media-breakpoint-up(xl) {
        margin: 0;
        width: 290px;
      }
    }
  }

  .content-section {
    > .hr {
      margin-left: 10px;
      margin-right: 10px;
      opacity: 0.5;

      @include media-breakpoint-up(sm) {
        margin-left: 0;
        margin-right: 0;
      }
    }

    > .faqs {
      @include font-weight-medium;
      margin-bottom: 20px;
      display: flex;
      justify-content: center;
      > .arrow {
        width: 0;
        height: 0;
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-left: 6px solid red;
        margin-top: 2px;
        margin-left: 7px;
      }
    }
  }

  .header-section.new-banner {
    padding-top: 0;
    height: auto;

    @include media-breakpoint-down(sm) {
      height: 500px;
      background-size: contain;

      @include media-breakpoint-only(xs) {
        margin-bottom: 0;  // mobile
      }
    }

    .h5 {
      font-size: 40px;
      width: 100%;
      @include media-breakpoint-down(sm) {
        font-size: 30px;
      }
      @include media-breakpoint-down(lg) {
        font-size: 30px;
      }
    }
  }

  .header-wrapper {
    background-color: $white;
    margin: 15px auto;
    padding: 25px;

    @include media-breakpoint-down(sm) {
      background-size: cover;
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: 40px;
      width: 100%;
      padding: 10px;
    }

    @include media-breakpoint-down(lg) {
      &.with-user {
        padding: 15px;
        height: 95%;
      }
    }

    @include media-breakpoint-only(xs) {
      padding: 10px;  // mobile
      &.with-user {
        height: auto;
      }
    }

    .h5 {
      width: 100%;
      @include media-breakpoint-down(sm) {
        font-size: 30px;
      }
      @include media-breakpoint-up(lg) {
        font-size: 40px;
      }
    }

    .btn {
      @include media-breakpoint-up(sm) {
        font-size: 13px;
      }
    }

    .header-section {
      @include media-breakpoint-down(sm) {
        margin-bottom: 20px;
      }
    }
  }
</style>
